import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { readCookieRaw } from '../../utils/cookies'
import Image from '../imgix-img'
import { getValue } from '../../utils/product-schema'
import Button from '../../atoms/button'
import { useEntities } from '../helpers/selectors'
import { FsCore } from 'types/fs-core'
import Icon from '../../atoms/icon'
import CategoryPreview from '../category-preview'
import CallOut from '../callout'
import { pathname } from '../../redux/app-selectors'
import { CircularPercentageBar } from '../product-table-v2/cell'
import classNames from 'classnames'

const categoryMapping = {
  'business-savings': {
    label: 'Business Savings',
    schemaCategory: 'business-savings',
    icon: 'business-savings',
    resultsPage: '/business/business-savings-accounts/results#business-savings-account-deals',
  },
  'business-current-accounts-1-year-plus': {
    label: 'Business Bank Account',
    schemaCategory: 'business-current-accounts',
    icon: 'business-current-accounts',
    resultsPage: '/business/bank-accounts/results',
  },
  'business-current-accounts': {
    label: 'Business Bank Account',
    schemaCategory: 'business-current-accounts',
    icon: 'business-current-accounts',
    resultsPage: '/business/bank-accounts/results',
  },
  'business-insurance': {
    label: 'Business Insurance',
    schemaCategory: 'business-insurance',
    icon: 'business-insurance',
  },
  'business-loans': {
    label: 'Business Loans',
    schemaCategory: 'business-loans',
    icon: 'business-loans',
    resultsPage: '/business/business-loans/results',
  },
  'startup-business-loans': {
    label: 'Startup Business Loans',
    schemaCategory: 'business-loans',
    icon: 'business-loans',
    resultsPage: '/business/business-loans/start-up-business-loans#startup-business-loans-deals',
  },
  'invoice-financing': {
    label: 'Invoice Financing',
    schemaCategory: 'invoice-financing',
    icon: 'invoice-financing',
    resultsPage: '/business/business-loans/invoice-finance#invoice-finance-deals',
  },
  'invoice-financing-triver': {
    label: 'Advance your invoices',
    schemaCategory: 'cash-flow-financing',
    icon: 'invoice-financing',
    resultsPage: '/business/business-loans/invoice-finance#invoice-finance-deals',
  },
  'business-credit-cards': {
    label: 'Business Credit Cards',
    schemaCategory: 'business-credit-cards',
    icon: 'business-credit-cards',
    resultsPage: '/business/business-credit-cards/results',
  },
  'credit-reports-broker': {
    label: 'Credit Report Broker',
    schemaCategory: 'credit-reports-broker',
    icon: 'business-credit-cards',
  },
  'business-electricity-insurance-broker': {
    label: 'Business Energy',
    schemaCategory: 'business-electricity-insurance-broker',
    icon: 'business-electricity',
  },
}

const fetchResults = async (deviceId: string, landingPage: string | null) => {
  const { __FS_API_URL__ } = global
  const apiPath: string = __FS_API_URL__ || 'https://www.money.co.uk/api'
  const results = await fetch(
    `${apiPath}/sme-journey/results?deviceId=${deviceId}&brand=money${
      landingPage ? `&landingPage=${landingPage}` : ''
    }`,
  )
  if (results.ok) {
    const data = await results.json()

    return data
  }
  if (results.status === 404) {
    window.location.href = '/business' // if no results redirect to business hub
    throw Error('Reults not found')
  }
  return null
}

interface Product {
  product: FsCore.ESQueryData
  productCategory: string
}

interface Cell {
  label: object
  content: object
}
export interface Schema {
  category: string
  badge: object
  image: object
  title: object
  eligibility: object
  primaryCta: {
    href: object
    text: object
  }
  description: object
}

interface ContentfulSchema {
  schema: {
    category: string
    badge: object
    image: object
    title: object
    eligibility: object
    primaryCta: {
      href: object
      text: object
    }
    tagline?: string
    cells: Cell[]
    description: object
  }
}

const addPageParam = (href: string, pathname: string) => {
  try {
    const url = new URL(href)
    url.searchParams.set('page', pathname)
    return url.toString()
  } catch (e) {
    return href
  }
}

const SuggestedProducts = ({
  schemas,
  pathname,
}: {
  schemas: ContentfulSchema[]
  pathname: string
  variant?: string
}) => {
  const [loading, setLoading] = useState(true)
  const [products, setProducts] = useState<Product[] | null>(null)
  const productSchemas: ContentfulSchema[] = useEntities(schemas)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const landingPage = sessionStorage.getItem('landingPage')
      const deviceId = readCookieRaw('uscd')

      fetchResults(deviceId, landingPage)
        .then((d) => {
          setProducts(d)
          setLoading(false)
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
    }
  }, [])

  return (
    <>
      <div className='SuggestedProducts'>
        {loading && (
          <div className='SuggestedProducts__skeleton'>
            <div className='SuggestedProducts__skeleton__product' />
            <div className='SuggestedProducts__skeleton__product' />
            <div className='SuggestedProducts__skeleton__product' />
          </div>
        )}
        {products && !loading && (
          <div className='SuggestedProducts__products'>
            {products.slice(0, 3).map((product, key) => {
              const productData = product.product.hits.hits[0]
              const category = product.productCategory
              const schema = productSchemas.find(
                (schema: Record<string, any>) =>
                  schema.schema.category === categoryMapping[category]?.schemaCategory,
              )?.schema
              if (!schema) {
                return <div key={key} />
              }
              const primaryCta = Array.isArray(schema.primaryCta)
                ? schema.primaryCta.reduce(
                    (result, { href, text }) => {
                      const hrefValue = getValue(href, productData)
                      const textValue = getValue(text, productData)
                      return !result.href && hrefValue
                        ? { href: hrefValue, text: textValue }
                        : result
                    },
                    { href: null, text: null },
                  )
                : {
                    href: getValue(schema.primaryCta.href, productData),
                    text: getValue(schema.primaryCta.text, productData),
                  }
              const primaryCtaHref = addPageParam(primaryCta.href, pathname)
              const easyApply =
                primaryCta.text === 'Apply online' && !!readCookieRaw('sme-session-id')
              return (
                <div
                  className={classNames('SuggestedProducts__products__product', {
                    'SuggestedProducts__products__product--easyApply': easyApply,
                  })}
                  key={key}
                >
                  {schema.eligibility &&
                    getValue(schema.eligibility.eligibilityPercent, productData) && (
                      <div className='SuggestedProducts__products__product__eligibility'>
                        <CircularPercentageBar
                          percent={getValue(schema.eligibility.eligibilityPercent, productData)}
                          color='#00892B'
                        />
                        <div className='SuggestedProducts__products__product__eligibility__text'>
                          {
                            schema.eligibility.text[
                              getValue(schema.eligibility.eligibilityPercent, productData)
                            ]
                          }
                        </div>
                      </div>
                    )}
                  {easyApply && (
                    <div className='SuggestedProducts__products__product__easyApply'>
                      ⭐️ Easy Apply
                    </div>
                  )}
                  <div className='SuggestedProducts__products__product__detailsWrapper'>
                    <div className='SuggestedProducts__products__product__title'>
                      {getValue(schema.title, productData)}
                    </div>
                    {getValue(schema.badge, productData) && (
                      <div className='SuggestedProducts__products__product__usp'>
                        <div className='SuggestedProducts__products__product__usp--icon'>
                          <Icon glyph={'star-banner'} color='#34454e' size={24} />
                        </div>

                        <div>{getValue(schema.badge, productData)}</div>
                      </div>
                    )}
                    <div className='SuggestedProducts__products__product__cells'>
                      {schema.cells.map(
                        (cell: { label: object; content: object }, cellKey: number) => {
                          return getValue(cell.content, productData) ? (
                            <div
                              key={cellKey}
                              className='SuggestedProducts__products__product__cells__cell'
                            >
                              <div className='SuggestedProducts__products__product__cells__cell--label'>
                                {getValue(cell.label, productData)}
                              </div>
                              <div className='SuggestedProducts__products__product__cells__cell--content'>
                                {getValue(cell.content, productData)}
                              </div>
                            </div>
                          ) : (
                            <></>
                          )
                        },
                      )}
                    </div>
                    {getValue(schema.description, productData) && (
                      <div>{getValue(schema.description, productData)}</div>
                    )}
                    {easyApply && (
                      <div>
                        {' '}
                        ⭐️ Some of your details will be pre-filled when you move to the next page.
                      </div>
                    )}
                    <Image
                      src={getValue(schema.image, productData)}
                      width={300}
                      alt='provider logo'
                      critical={true}
                    />
                    <Button
                      href={primaryCtaHref}
                      variant={easyApply ? 'primary' : 'secondary'}
                      size='md'
                      openInNewTab={true}
                      position={key}
                      withIcon={true}
                    >
                      {primaryCta.text}
                    </Button>
                    {categoryMapping[category]?.resultsPage ? (
                      <a
                        href={categoryMapping[category]?.resultsPage}
                        target='_blank'
                        className='SuggestedProducts__products__product__resultsPage'
                        rel='noreferrer'
                      >
                        View other providers
                      </a>
                    ) : (
                      <div />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      {products &&
        !loading &&
        products.some(
          (p) =>
            p.productCategory.includes('business-credit-cards') ||
            p.productCategory.includes('business-loans') ||
            p.productCategory.includes('invoice-financing'),
        ) && (
          <CallOut
            text='Applying for several credit products in a short time can hurt your credit score and reduce approval chances. Consider spacing out applications for better results.'
            variant='platinum'
            bold={true}
          />
        )}
      {products && !loading && (
        <>
          <h3 className='SuggestedProducts__otherProducts'>
            {' '}
            Want more? See some other business products that could help your business
          </h3>
          <div className='SuggestedProducts__categoryPreview'>
            <CategoryPreview
              title={categoryMapping[products.slice(-1)[0].productCategory]?.label}
              description={`Here are our three top picks for ${categoryMapping[
                products.slice(-1)[0].productCategory
              ]?.label?.toLowerCase()}`}
              resultsPage={categoryMapping[products.slice(-1)[0].productCategory]?.resultsPage}
              category
              products={products.slice(-1)[0].product.hits.hits}
              schema={
                productSchemas.find(
                  (s) =>
                    s.schema.category ===
                    categoryMapping[products.slice(-1)[0].productCategory]?.schemaCategory,
                )?.schema
              }
            />
          </div>
        </>
      )}
    </>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    pathname: pathname(state),
    ...props,
  }
})(SuggestedProducts)
